import React, { useState } from 'react';
import { reportIncident } from '../../services/incidentService';

/**
 * ReportIssueModal
 *
 * A minimal modal for users to report bugs or issues.
 * Can be triggered from anywhere in the app.
 *
 * Usage:
 *   <ReportIssueModal isOpen={showModal} onClose={() => setShowModal(false)} />
 */
const ReportIssueModal = ({ isOpen, onClose }) => {
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('bug');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  if (!isOpen) return null;

  const isDarkMode = document.body.classList.contains('dark-mode');
  const bg = isDarkMode ? '#1a1a1a' : '#ffffff';
  const fg = isDarkMode ? '#e0e0e0' : '#000000';
  const muted = isDarkMode ? '#666' : '#999';
  const borderColor = isDarkMode ? '#333' : '#e0e0e0';
  const inputBg = isDarkMode ? '#2d2d2d' : '#f5f5f5';
  const overlayBg = 'rgba(0, 0, 0, 0.5)';

  const handleSubmit = async () => {
    if (!description.trim()) return;
    setSubmitting(true);

    try {
      const severityMap = { bug: 'medium', crash: 'high', feature: 'low', other: 'low' };

      await reportIncident({
        type: category === 'crash' ? 'crash' : 'bug',
        severity: severityMap[category] || 'medium',
        description: description.trim(),
        metadata: { category, reportedVia: 'modal' },
      });
    } catch {
      // Silently handle — report best-effort, don't block the user
    }

    setSubmitting(false);
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setDescription('');
      setCategory('bug');
      onClose();
    }, 1500);
  };

  const handleClose = () => {
    if (!submitting) {
      setDescription('');
      setCategory('bug');
      setSubmitted(false);
      onClose();
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: overlayBg,
        fontFamily: '"IBM Plex Mono", monospace',
      }}
      onClick={handleClose}
    >
      <div
        style={{
          backgroundColor: bg,
          color: fg,
          border: `1px solid ${borderColor}`,
          width: '100%',
          maxWidth: '440px',
          margin: '0 16px',
          boxShadow: '3px 3px 0 #000',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '14px 18px',
            borderBottom: `1px solid ${borderColor}`,
            backgroundColor: '#000',
          }}
        >
          <h3 style={{ fontSize: '13px', fontWeight: '700', color: '#fff', margin: 0, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Report Issue
          </h3>
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#fff',
              fontSize: '18px',
              cursor: 'pointer',
              padding: '0 4px',
              lineHeight: 1,
            }}
          >
            x
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '18px' }}>
          {submitted ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <p style={{ fontSize: '14px', fontWeight: '600' }}>Report sent. Thank you.</p>
              <p style={{ fontSize: '12px', color: muted, marginTop: '6px' }}>Our team will look into it.</p>
            </div>
          ) : (
            <>
              {/* Category selector */}
              <div style={{ marginBottom: '14px' }}>
                <label style={{ fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', color: muted, display: 'block', marginBottom: '6px' }}>
                  Category
                </label>
                <div style={{ display: 'flex', gap: '6px' }}>
                  {[
                    { value: 'bug', label: 'Bug' },
                    { value: 'crash', label: 'Crash' },
                    { value: 'feature', label: 'Request' },
                    { value: 'other', label: 'Other' },
                  ].map(({ value, label }) => (
                    <button
                      key={value}
                      onClick={() => setCategory(value)}
                      style={{
                        padding: '6px 12px',
                        fontSize: '11px',
                        fontFamily: '"IBM Plex Mono", monospace',
                        fontWeight: category === value ? '700' : '400',
                        backgroundColor: category === value ? (isDarkMode ? '#fff' : '#000') : 'transparent',
                        color: category === value ? (isDarkMode ? '#000' : '#fff') : muted,
                        border: `1px solid ${category === value ? 'transparent' : borderColor}`,
                        borderRadius: '0px',
                        cursor: 'pointer',
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Description */}
              <div style={{ marginBottom: '18px' }}>
                <label style={{ fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', color: muted, display: 'block', marginBottom: '6px' }}>
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe the issue..."
                  autoFocus
                  style={{
                    width: '100%',
                    minHeight: '100px',
                    padding: '10px',
                    fontSize: '13px',
                    fontFamily: '"IBM Plex Mono", monospace',
                    backgroundColor: inputBg,
                    color: fg,
                    border: `1px solid ${borderColor}`,
                    borderRadius: '0px',
                    resize: 'vertical',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              {/* Submit */}
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button
                  onClick={handleClose}
                  style={{
                    padding: '8px 16px',
                    fontSize: '12px',
                    fontFamily: '"IBM Plex Mono", monospace',
                    backgroundColor: 'transparent',
                    color: muted,
                    border: `1px solid ${borderColor}`,
                    borderRadius: '0px',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={!description.trim() || submitting}
                  style={{
                    padding: '8px 16px',
                    fontSize: '12px',
                    fontFamily: '"IBM Plex Mono", monospace',
                    fontWeight: '600',
                    backgroundColor: isDarkMode ? '#fff' : '#000',
                    color: isDarkMode ? '#000' : '#fff',
                    border: 'none',
                    borderRadius: '0px',
                    cursor: !description.trim() || submitting ? 'not-allowed' : 'pointer',
                    opacity: !description.trim() || submitting ? 0.5 : 1,
                  }}
                >
                  {submitting ? 'Sending...' : 'Submit'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportIssueModal;
